/**
 * Smart Insights Panel
 *
 * Display AI-generated business insights with trends and recommendations
 */

import { Card, Space, Typography, Tag, Button, Statistic, Row, Col, Tooltip } from 'antd';
import {
  BulbOutlined,
  RiseOutlined,
  FallOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { SmartInsight } from '../../types/ai';
import useAIStore from '../../store/aiStore';
import useThemeStore from '../../store/themeStore';
import { dismissInsight as dismissInsightAPI } from '../../api/ai';
import { message } from 'antd';

const { Text, Paragraph } = Typography;

// Map severity to color and icon
const SEVERITY_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  critical: { color: '#ff4d4f', icon: <WarningOutlined /> },
  warning: { color: '#faad14', icon: <WarningOutlined /> },
  info: { color: '#1890ff', icon: <InfoCircleOutlined /> },
  success: { color: '#52c41a', icon: <CheckCircleOutlined /> },
};

// Map insight type to icon
const TYPE_ICONS: Record<string, React.ReactNode> = {
  trend: <RiseOutlined />,
  anomaly: <WarningOutlined />,
  recommendation: <BulbOutlined />,
  alert: <WarningOutlined />,
  opportunity: <CheckCircleOutlined />,
};

interface SmartInsightsPanelProps {
  insights: SmartInsight[];
}

export default function SmartInsightsPanel({ insights }: SmartInsightsPanelProps) {
  const { dismissInsight } = useAIStore();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const handleDismiss = async (insightId: string) => {
    try {
      await dismissInsightAPI(insightId, 'Dismissed by user');
      dismissInsight(insightId);
      message.success('Insight dismissed');
    } catch (error) {
      message.error('Failed to dismiss insight');
    }
  };

  if (insights.length === 0) return null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <BulbOutlined style={{ color: '#faad14' }} />
          <Text strong>Smart Insights</Text>
        </Space>
      }
      styles={{ body: { padding: '8px 12px' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {insights.map((insight) => {
          const severityConfig = SEVERITY_CONFIG[insight.severity];

          return (
            <div
              key={insight.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px',
                background: isDark ? '#1f1f1f' : '#fafafa',
                borderRadius: 6,
                border: `1px solid ${severityConfig.color}20`,
              }}
            >
              <div
                style={{
                  fontSize: 20,
                  color: severityConfig.color,
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                {TYPE_ICONS[insight.type] || severityConfig.icon}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Space size="small" wrap>
                    <Text strong style={{ fontSize: 13 }}>
                      {insight.title}
                    </Text>
                    <Tag
                      color={severityConfig.color}
                      style={{ fontSize: 10, padding: '0 4px', margin: 0 }}
                    >
                      {insight.type.toUpperCase()}
                    </Tag>
                  </Space>

                  <Paragraph
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
                    }}
                  >
                    {insight.description}
                  </Paragraph>

                  {/* Data visualization */}
                  {insight.data && (
                    <Row gutter={8} style={{ marginTop: 4 }}>
                      {insight.data.currentValue !== undefined && (
                        <Col>
                          <Statistic
                            title={insight.data.metric}
                            value={insight.data.currentValue}
                            precision={2}
                            prefix={insight.category.includes('revenue') || insight.category.includes('expense') ? '$' : ''}
                            valueStyle={{ fontSize: 14 }}
                            style={{ fontSize: 11 }}
                          />
                        </Col>
                      )}
                      {insight.data.changePercent !== undefined && (
                        <Col>
                          <Statistic
                            title="Change"
                            value={Math.abs(insight.data.changePercent)}
                            precision={1}
                            suffix="%"
                            prefix={
                              insight.data.changePercent > 0 ? (
                                <RiseOutlined style={{ color: '#52c41a' }} />
                              ) : (
                                <FallOutlined style={{ color: '#ff4d4f' }} />
                              )
                            }
                            valueStyle={{
                              fontSize: 14,
                              color: insight.data.changePercent > 0 ? '#52c41a' : '#ff4d4f',
                            }}
                            style={{ fontSize: 11 }}
                          />
                        </Col>
                      )}
                    </Row>
                  )}

                  <div style={{ marginTop: 4 }}>
                    <Tooltip title="Dismiss this insight">
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => handleDismiss(insight.id)}
                        style={{ fontSize: 12, padding: '0 4px' }}
                      >
                        Dismiss
                      </Button>
                    </Tooltip>
                  </div>
                </Space>
              </div>
            </div>
          );
        })}
      </Space>
    </Card>
  );
}
